import { useNavigate } from 'react-router-dom';
import { Lock, Users } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUpgradeWallStore } from '@/stores/upgradeWallStore';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

// v3.2 (21/05/26): Subscription tab moved to /profile?tab=billing.
// SettingsPage still redirects the legacy URL for backward compat.
const SUBSCRIPTION_PATH = '/profile?tab=billing';

function formatDate(iso: string | undefined, lang: 'it' | 'en'): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
}

export function UpgradeWallModal() {
  const { open, context, hide } = useUpgradeWallStore();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();

  // v3.4 (25/05/26) — Member confinement: an invited_member cannot upgrade.
  // Render an alternative modal that tells them to contact the plan owner.
  // The trial_expired hard-lock path is unreachable for members (they don't
  // have a personal trial; their access is gated by the owner's plan).
  const isMember = user?.canManagePlan === false;
  if (isMember && open) {
    const ownerLabel =
      user?.effectiveOwner?.displayName
      || user?.effectiveOwner?.email
      || t('profile.header.individual');
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) hide(); }}>
        <DialogContent
          aria-labelledby="upgrade-wall-member-title"
          aria-describedby="upgrade-wall-member-body"
          className="max-w-md"
        >
          <DialogHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-2">
              <Users className="h-6 w-6 text-muted-foreground" />
            </div>
            <DialogTitle id="upgrade-wall-member-title" className="text-center">
              {t('upgrade.member.title')}
            </DialogTitle>
            <DialogDescription id="upgrade-wall-member-body" className="text-center">
              {t('upgrade.member.description', { owner: ownerLabel })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button onClick={() => hide()} size="lg" variant="outline">
              {t('upgrade.member.cta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const reason = context?.reason ?? 'trial_expired';
  // v3.2 polish (21/05/26): BE sends both `pool_type` (generic, e.g.
  // 'structural') and `limit_type` (specific, e.g. 'seats'). Prefer
  // `limit_type` for the cap-specific copy, fall back to `pool_type`.
  const poolType = context?.limit_type ?? context?.pool_type;
  const isTrialExpired = reason === 'trial_expired';
  const trialEndedFmt = formatDate(context?.trial_ended_at, language);

  // v3.2 polish (21/05/26 ~23:10): the modal previously always rendered the
  // "Il tuo trial è terminato" copy regardless of reason — confusing for
  // paid customers who hit a structural cap (seats / positions / candidates).
  // Now title + body are reason-aware.
  let title: string = t('trial.upgradeWall.title');
  let body = '';
  if (reason === 'trial_expired') {
    title = t('trial.upgradeWall.title');
    body = trialEndedFmt
      ? `${t('trial.upgradeWall.trialExpiredBody')} (${trialEndedFmt})`
      : t('trial.upgradeWall.trialExpiredBody');
  } else if (reason === 'structural_limit') {
    if (poolType === 'seats') {
      title = t('upgradeWall.seats.title');
      body = t('upgradeWall.seats.body');
    } else if (poolType === 'positions') {
      title = t('upgradeWall.positions.title');
      body = t('upgradeWall.positions.body');
    } else if (poolType === 'candidates' || poolType === 'candidates_processed') {
      title = t('upgradeWall.candidates.title');
      body = t('upgradeWall.candidates.body');
    } else {
      title = t('upgradeWall.structural.title');
      body = t('upgradeWall.structural.body');
    }
  } else if (reason === 'insufficient_balance') {
    title = t('upgradeWall.balance.title');
    body = t('upgradeWall.balance.body');
  } else if (reason === 'plan_required') {
    title = t('upgradeWall.planRequired.title');
    body = t('upgradeWall.planRequired.body');
  } else {
    body = t('trial.upgradeWall.trialExpiredBody');
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && isTrialExpired) return; if (!v) hide(); }}>
      <DialogContent
        aria-labelledby="upgrade-wall-title"
        aria-describedby="upgrade-wall-body"
        className={cn('max-w-md', isTrialExpired && '[&>button.absolute]:hidden')}
        onPointerDownOutside={(e) => { if (isTrialExpired) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isTrialExpired) e.preventDefault(); }}
      >
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center mb-2">
            <Lock className="h-6 w-6 text-orange-600" />
          </div>
          <DialogTitle id="upgrade-wall-title" className="text-center">
            {title}
          </DialogTitle>
          <DialogDescription id="upgrade-wall-body" className="text-center">
            {body}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button
            onClick={() => { hide(); navigate(SUBSCRIPTION_PATH); }}
            size="lg"
          >
            {t('trial.upgradeWall.cta')} →
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeWallModal;